import { X } from "lucide-react";
import * as React from "react";
import { isIOS } from "react-device-detect";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "@krea/shared/utils";

import { Button } from "./Button";

const Drawer = ({
  shouldScaleBackground = false,
  handleOnly = true,
  // Keyboard handling is done in DrawerContent via visualViewport (vaul's
  // built-in repositioning shrinks the drawer to near-zero height instead).
  repositionInputs = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    handleOnly={handleOnly}
    repositionInputs={repositionInputs}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-white/80 backdrop-blur-xs", className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

interface IDrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  hideCloseButton?: boolean;
  overlayClassName?: string;
  /** className for the children wrapper div. Defaults to "px-4 pb-8". Pass "" to opt out of default padding. */
  contentClassName?: string;
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  IDrawerContentProps
>(
  (
    {
      className,
      children,
      hideCloseButton = false,
      overlayClassName,
      contentClassName,
      style,
      ...props
    },
    ref,
  ) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const panelRef = React.useRef<HTMLDivElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        panelRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    // Center the focused field in the scroll container's visible area, so it
    // can't sit hidden behind sticky/bottom-pinned buttons inside the drawer.
    const scrollFocusedFieldIntoView = React.useCallback(() => {
      const container = scrollRef.current;
      const el = document.activeElement as HTMLElement | null;
      if (!container || !el || !container.contains(el)) return;
      if (!el.matches("input, textarea, select, [contenteditable]")) return;
      const elRect = el.getBoundingClientRect();
      const cRect = container.getBoundingClientRect();
      const offset =
        elRect.top - cRect.top - (cRect.height - elRect.height) / 2;
      // Instant (not smooth) so the caret realignment that follows runs
      // against the final geometry.
      container.scrollTo({
        top: container.scrollTop + offset,
        behavior: "auto",
      });
    }, []);

    // iOS Safari draws the text caret and spell-check underlines in a native
    // overlay that is not recomputed when the field moves because we lifted
    // the panel or scrolled its container — leaving caret and underlines
    // offset from the text (WebKit #176896 family). A synchronous blur +
    // refocus forces WebKit to redraw the overlay at the field's current
    // position; the keyboard stays open because focus returns within the
    // same task. realigningRef stops the refocus from re-entering the
    // delegated onFocus handler below.
    const realigningRef = React.useRef(false);
    const realignCaret = React.useCallback(() => {
      if (!isIOS) return;
      const panel = panelRef.current;
      const el = document.activeElement;
      if (
        !panel ||
        !(
          el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
        ) ||
        !panel.contains(el)
      )
        return;
      const { selectionStart, selectionEnd, selectionDirection } = el;
      realigningRef.current = true;
      el.blur();
      el.focus({ preventScroll: true });
      realigningRef.current = false;
      const supportsSelection =
        el instanceof HTMLTextAreaElement ||
        /^(?:text|search|tel|url|password)$/.test(el.type);
      if (
        supportsSelection &&
        selectionStart !== null &&
        selectionEnd !== null
      ) {
        el.setSelectionRange(
          selectionStart,
          selectionEnd,
          selectionDirection ?? "none",
        );
      }
    }, []);

    // Lift the drawer above the software keyboard. The visualViewport shrinks
    // by exactly the keyboard height when it opens; we move the panel up by
    // that amount (via `bottom`) and cap its height to the remaining space.
    React.useEffect(() => {
      const vv = window.visualViewport;
      if (!vv) return;

      const onViewportChange = () => {
        const panel = panelRef.current;
        if (!panel) return;
        const keyboardHeight = Math.max(
          0,
          window.innerHeight - vv.height - vv.offsetTop,
        );
        // Threshold filters out URL-bar collapse and other minor viewport noise
        if (keyboardHeight > 60) {
          panel.style.bottom = `${keyboardHeight}px`;
          panel.style.maxHeight = `${vv.height - 24}px`;
          // Scroll after the panel has re-laid-out at its new size, so the
          // offsets are computed against the final geometry.
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              scrollFocusedFieldIntoView();
              realignCaret();
            }),
          );
        } else {
          panel.style.bottom = "";
          panel.style.maxHeight = "";
        }
      };

      vv.addEventListener("resize", onViewportChange);
      vv.addEventListener("scroll", onViewportChange);
      return () => {
        vv.removeEventListener("resize", onViewportChange);
        vv.removeEventListener("scroll", onViewportChange);
      };
    }, [scrollFocusedFieldIntoView, realignCaret]);

    return (
      <DrawerPortal>
        <DrawerOverlay className={overlayClassName} />
        <DrawerPrimitive.Content
          ref={setRefs}
          style={style}
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto max-h-[90svh] flex-col rounded-t-[10px] border bg-white",
            className,
          )}
          {...props}
        >
          <DrawerPrimitive.Handle className="mx-auto mt-3 mb-2 h-1.5 w-10 rounded-full bg-gray-300" />
          {!hideCloseButton && (
            <div className="flex justify-end px-2">
              <DrawerClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Close"
                  className="size-11 md:size-10"
                >
                  <X className="size-6" strokeWidth={2} />
                  <span className="sr-only">Close</span>
                </Button>
              </DrawerClose>
            </div>
          )}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: onFocus only delegates bubbled focus events from form fields inside — the div itself is not interactive */}
          <div
            ref={scrollRef}
            className={cn(
              "min-h-0 flex-1 overflow-y-auto overflow-x-hidden",
              "[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-3xl",
              contentClassName !== undefined ? contentClassName : "px-4 pb-8",
            )}
            onFocus={(e) => {
              if (realigningRef.current) return;
              if (
                !(e.target as HTMLElement).matches(
                  "input, textarea, select, [contenteditable]",
                )
              )
                return;
              // Fallback for when the keyboard is already open (focus moved
              // between fields — no visualViewport resize fires in that case).
              setTimeout(() => {
                scrollFocusedFieldIntoView();
                realignCaret();
              }, 100);
            }}
          >
            {children}
          </div>
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  },
);
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn("text-section leading-none tracking-tight", className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
